/**
 * Cancha Bundle Builder — Storefront Engine
 * Vanilla JS · Web Components · No dependencies · ~15KB
 *
 * Wrapped in an IIFE with a global guard so the script can be safely
 * included by multiple theme app blocks on the same page (bundle-builder
 * + bundle-contents) without redeclaring `$` etc. or double-binding
 * event handlers.
 */
'use strict';

(function () {
  if (window.__canchaBundleLoaded) return;
  window.__canchaBundleLoaded = true;

// ── Utilities ─────────────────────────────────────────────────

const $ = (sel, root = document) => root.querySelector(sel);
const $$ = (sel, root = document) => [...root.querySelectorAll(sel)];
const uid = () => 'cb_' + Math.random().toString(36).slice(2, 9);
const esc = (s) => String(s ?? '').replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
const upgradeProperty = (element, property) => {
  if (!Object.prototype.hasOwnProperty.call(element, property)) return;
  const value = element[property];
  delete element[property];
  element[property] = value;
};
const fmt = (cents) => {
  const fmt = window.Shopify?.money_format || '${{amount}}';
  const amt = (cents / 100).toFixed(2);
  return fmt
    .replace('{{amount}}', amt)
    .replace('{{amount_no_decimals}}', Math.floor(cents / 100))
    .replace('{{amount_with_comma_separator}}', amt.replace('.', ','));
};

// ── Toast ─────────────────────────────────────────────────────

const Toast = {
  _el: null,
  _timer: null,
  // Use the Popover API so the toast renders in its own top-layer slot,
  // appearing above native <dialog> elements opened via showModal().
  _usePopover: typeof HTMLElement !== 'undefined' && 'popover' in HTMLElement.prototype,
  show(msg, ms = 3000) {
    if (!this._el) {
      this._el = Object.assign(document.createElement('div'), { className: 'cb-toast' });
      if (this._usePopover) this._el.setAttribute('popover', 'manual');
      document.body.appendChild(this._el);
    }
    this._el.textContent = msg;
    clearTimeout(this._timer);
    if (this._usePopover) {
      try { this._el.hidePopover(); } catch (_) {}
      try { this._el.showPopover(); } catch (_) {}
    }
    this._el.dataset.show = '';
    this._timer = setTimeout(() => {
      delete this._el.dataset.show;
      if (this._usePopover) try { this._el.hidePopover(); } catch (_) {}
    }, ms);
  },
};

// ── Cart API ──────────────────────────────────────────────────

// Ask the theme which sections to re-render (the cart drawer's real ID is
// e.g. "sections--123__cart-drawer", not "cart-drawer"), so the drawer can
// update straight from the add response.
function bundledSections() {
  const sections = new Set();
  document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections } }));
  return [...sections].join(',');
}

const Cart = {
  async add(items) {
    const r = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items, sections: bundledSections() }),
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data.description || 'Could not add to cart');
    return data;
  },
};

// ── Bundle State ──────────────────────────────────────────────

class BundleState extends EventTarget {
  constructor(config) {
    super();
    this.config = config;
    // key: `${variantId}__${categoryId}` → { product, variant, categoryId, qty }
    this.selections = new Map();
    this.instanceId = uid();
  }

  get totalQty() {
    let n = 0;
    for (const v of this.selections.values()) n += v.qty;
    return n;
  }

  get totalCents() {
    let n = 0;
    for (const v of this.selections.values()) n += v.variant.price * v.qty;
    return n;
  }

  categoryCount(catId) {
    let n = 0;
    for (const v of this.selections.values()) {
      if (v.categoryId === catId) n += v.qty;
    }
    return n;
  }

  canAdd(catId) {
    const cat = this.config.categories?.find(c => c.id === catId);
    if (!cat) return true;
    return this.categoryCount(catId) < (cat.maxQty ?? Infinity);
  }

  isComplete() {
    if (this.config.mode === 'fixed') return true;
    const cats = this.config.categories;
    if (!cats?.length) return this.totalQty >= (this.config.minQty ?? 1);
    return cats.every(cat => {
      const n = this.categoryCount(cat.id);
      return !cat.required || n >= (cat.minQty ?? 1);
    });
  }

  add(product, variant, catId) {
    if (!this.canAdd(catId)) return false;
    const key = `${variant.id}__${catId}`;
    if (this.selections.has(key)) {
      this.selections.get(key).qty++;
    } else {
      this.selections.set(key, { product, variant, categoryId: catId, qty: 1 });
    }
    this._emit();
    return true;
  }

  remove(key) {
    this.selections.delete(key);
    this._emit();
  }

  decrement(key) {
    const item = this.selections.get(key);
    if (!item) return;
    item.qty > 1 ? item.qty-- : this.selections.delete(key);
    this._emit();
  }

  changeVariant(oldKey, newVariant) {
    const item = this.selections.get(oldKey);
    if (!item) return;
    this.selections.delete(oldKey);
    item.variant = newVariant;
    const newKey = `${newVariant.id}__${item.categoryId}`;
    this.selections.set(newKey, item);
    this._emit();
  }

  clear() { this.selections.clear(); this._emit(); }

  _emit() { this.dispatchEvent(new CustomEvent('change', { detail: this })); }

  /** Returns the discount % the cart should apply right now (sidebar preview) */
  computeDiscountPct() {
    const { type, value, tiers } = this.config.pricing ?? {};
    if (type === 'percentage' && value > 0) return Math.min(100, value);
    if (type === 'fixed' && this.totalCents > 0) {
      // Prefer the presentment-currency parent variant price (from Liquid, Markets-safe).
      // Fall back to the admin-set value (shop currency) for legacy configs.
      const fixedPrice = this.config.parentVariantPrice || value;
      const fixedCents = Math.round(fixedPrice * 100);
      if (fixedCents > 0 && fixedCents < this.totalCents) {
        return ((this.totalCents - fixedCents) / this.totalCents) * 100;
      }
      return 0;
    }
    if (type === 'tiered' && Array.isArray(tiers) && this.totalQty > 0) {
      const matching = tiers
        .filter((t) => this.totalQty >= (t.qty || 0))
        .reduce((best, t) => ((t.percent || 0) > (best?.percent || 0) ? t : best), null);
      return matching?.percent ?? 0;
    }
    return 0;
  }

  /** Builds a single parent-variant cart line with encoded selections.
   *  The cart-transform function expands this into component lines at cart time,
   *  reading all pricing rules from the server-authoritative product metafield.
   *  This prevents client-side forgery of discounts, parent variant, or components.
   */
  toCartItems() {
    // For tiered pricing, pick the variant matching the customer's total qty
    // (highest tier they qualify for). Falls back to parent_variant_id otherwise.
    const tierVariants = Array.isArray(this.config.tierVariants) ? this.config.tierVariants : null;
    let parentVariantId = this.config.parentVariantId || '';
    if (tierVariants && tierVariants.length > 0) {
      const totalQty = this.totalQty;
      const eligible = tierVariants
        .filter((t) => totalQty >= (t.qty || 0))
        .sort((a, b) => (b.qty || 0) - (a.qty || 0))[0];
      if (eligible?.variant_id) parentVariantId = eligible.variant_id;
    }
    if (!parentVariantId) return [];
    const parentVid = parseInt(parentVariantId.split('/').pop() || '0');
    if (!parentVid) return [];

    // Encode each selection with enough data for the cart transform to:
    //   p  — product GID: validate against metafield category allowlist
    //   k  — category ID: validate per-category min/max quantity constraints
    //   v  — variant GID: the chosen variant to expand into
    //   q  — quantity
    //   c  — natural price in cents (presentment currency): proportional allocation for fixed-price bundles
    const selections = [...this.selections.values()].map((item) => ({
      v: item.variant.id,
      p: item.product.id
        ? `gid://shopify/Product/${item.product.id}`
        : undefined,
      k: item.categoryId,
      q: item.qty,
      c: item.variant.price ?? 0,
    }));

    return [{
      id: parentVid,
      quantity: 1,
      properties: {
        '_cancha_bundle_selections': JSON.stringify(selections),
        '_cancha_bundle_title': this.config.title ?? 'Bundle',
        '_cancha_bundle_mode': this.config.mode ?? 'variable',
      },
    }];
  }
}

// ── CanchaBundleBuilder (root element) ────────────────────────

class CanchaBundleBuilder extends HTMLElement {
  connectedCallback() {
    if (this._connected) return;
    this._connected = true;

    const cfg = $('[data-bundle-config]', this);
    if (!cfg) return;
    try { this.config = JSON.parse(cfg.textContent); }
    catch { console.error('[Cancha Bundle] Invalid config'); return; }

    this.state = new BundleState(this.config);
    this.state.addEventListener('change', () => this._sync());
    this.fixedOverrides = {};

    if (this.config.mode === 'fixed') {
      this._initFixedConfigurator();
      this._initButtons();
      return;
    }

    // Wire children after they've connected
    this._cards = [];
    this._sidebar = $('cancha-bundle-sidebar', this);
    if (this._sidebar) this._sidebar.state = this.state;

    $$('cancha-bundle-card', this).forEach(card => {
      card.builder = this;
      card.state = this.state;
      this._cards.push(card);
    });

    this._initButtons();
    this._initMobileDrawer();
    this._sync();
  }

  // ── Fixed-bundle variant configurator ──────────────────────

  _initFixedConfigurator() {
    const items = this.config.bundleComponents?.items ?? [];
    const configurable = items.filter(i => i.configurable && Array.isArray(i.variants) && i.variants.length > 1);
    if (configurable.length === 0) return;

    const container = $('[data-cb-fix-config]', this);
    if (!container) return;

    // Pre-select first available variant for each slot
    for (const item of configurable) {
      const first = item.variants.find(v => v.available) ?? item.variants[0];
      if (first) this.fixedOverrides[item.variant_id] = first.id;
    }

    container.innerHTML = configurable.map(item => `
      <div class="cb-fix-picker" data-component="${esc(item.variant_id)}">
        <p class="cb-fix-picker__label">${esc(item.product_title ?? 'Component')}</p>
        <div class="cb-fix-picker__options" role="group">
          ${item.variants.map(v => {
            const pre = this.fixedOverrides[item.variant_id] ?? item.variants.find(x => x.available)?.id;
            const selected = v.id === pre;
            return `<button
              class="cb-fix-picker__opt${selected ? ' cb-fix-picker__opt--selected' : ''}${!v.available ? ' cb-fix-picker__opt--unavailable' : ''}"
              data-component="${esc(item.variant_id)}"
              data-variant="${esc(v.id)}"
              type="button"
              ${!v.available ? 'disabled aria-disabled="true"' : ''}
            >${esc(v.title)}</button>`;
          }).join('')}
        </div>
      </div>
    `).join('');

    $$('.cb-fix-picker__opt', container).forEach(btn => {
      btn.addEventListener('click', () => {
        if (btn.disabled) return;
        const comp = btn.dataset.component;
        $$(`[data-component="${comp}"]`, container).forEach(b =>
          b.classList.toggle('cb-fix-picker__opt--selected', b === btn)
        );
        this.fixedOverrides[comp] = btn.dataset.variant;
        this._syncFixedATC();
      });
    });

    this._syncFixedATC();
  }

  _syncFixedATC() {
    const items = this.config.bundleComponents?.items ?? [];
    const configurable = items.filter(i => i.configurable && Array.isArray(i.variants) && i.variants.length > 1);
    const allSelected = configurable.every(i => this.fixedOverrides[i.variant_id]);
    $$('[data-cb-atc]', this).forEach(b => { b.disabled = !allSelected; });
  }

  _initButtons() {
    $$('[data-cb-atc]', this).forEach(btn =>
      btn.addEventListener('click', () => this._addToCart())
    );
  }

  _initMobileDrawer() {
    const bar      = $('.cb-drawer-bar', this);
    const backdrop = $('.cb-backdrop', this);
    const drawer   = $('.cb-drawer', this);
    if (!bar) return;

    // Open
    bar.addEventListener('click', (e) => {
      if (e.target.closest('[data-cb-atc]')) return;
      if (backdrop) backdrop.dataset.open = '';
      if (drawer) drawer.dataset.open = '';
      document.body.style.overflow = 'hidden';
    });

    // Close
    backdrop?.addEventListener('click', () => this._closeDrawer());
    $('[data-cb-drawer-close]', this)?.addEventListener('click', () => this._closeDrawer());
  }

  _closeDrawer() {
    const backdrop = $('.cb-backdrop', this);
    const drawer   = $('.cb-drawer', this);
    delete backdrop?.dataset.open;
    delete drawer?.dataset.open;
    document.body.style.overflow = '';
  }

  async _addToCart() {
    const fixedMode = this.config.mode === 'fixed';
    if (!fixedMode && !this.state.isComplete()) {
      Toast.show('Please complete all required categories');
      return;
    }

    const btns = $$('[data-cb-atc]', this);
    btns.forEach(b => { b.classList.add('cb-btn--loading'); b.disabled = true; });

    try {
      let items;
      if (fixedMode) {
        const variantGid = this.config.parentVariantId || '';
        const variantId = parseInt(variantGid.split('/').pop() || '');
        if (!variantId) throw new Error('Bundle product variant is not configured');
        const properties = {};
        const overrides = this.fixedOverrides ?? {};
        if (Object.keys(overrides).length > 0) {
          properties['_cancha_fixed_overrides'] = JSON.stringify(overrides);
        }
        items = [{ id: variantId, quantity: 1, properties }];
      } else {
        items = this.state.toCartItems();
        if (!items.length) {
          throw new Error(
            this.state.selections.size > 0
              ? 'Bundle product variant is not configured — re-save the bundle in the app'
              : 'No items selected'
          );
        }
      }
      const result = await Cart.add(items);

      // Mirror the theme's own ATC event pattern (assets/theme.js dispatches
      // cart:change on documentElement with baseEvent: "variant:add"). The
      // CartDrawer listens for this exact shape and shows itself when
      // settings.cartType === "drawer". Other themes also commonly listen,
      // so we keep a few legacy fallbacks below.
      try {
        const cartContent = await (await fetch(`${window.Shopify.routes.root}cart.js`)).json();
        cartContent.sections = result.sections ?? cartContent.sections ?? {};
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: { baseEvent: 'variant:add', cart: cartContent },
        }));
      } catch (e) {
        console.warn('[Cancha Bundle] cart:change dispatch failed', e);
      }

      // Legacy fallbacks for themes that listen to other event names.
      document.dispatchEvent(new CustomEvent('cart:refresh'));
      document.dispatchEvent(new CustomEvent('cart:open'));
      document.dispatchEvent(new CustomEvent('cart-drawer:open'));
      const drawerEl = document.querySelector('cart-drawer, cart-notification, [data-cart-drawer]');
      if (drawerEl) {
        if (typeof drawerEl.show === 'function') { try { drawerEl.show(); } catch (_) {} }
        else if (typeof drawerEl.open === 'function') { try { drawerEl.open(); } catch (_) {} }
      }

      this._closeDrawer();
      const modal = this.closest('.cb-modal') ?? document.querySelector('.cb-modal[open]');
      if (modal) closeCanchaModal(modal);
      Toast.show(this.config.addedText ?? 'Bundle added to cart!');
      if (this.config.clearAfterAdd) this.state.clear();
    } catch (err) {
      console.error('[Cancha Bundle] Cart add failed', err);
      Toast.show(err.message || 'Could not add bundle to cart');
    } finally {
      btns.forEach(b => { b.classList.remove('cb-btn--loading'); b.disabled = false; });
    }
  }

  _sync() {
    const done = this.state.isComplete();
    $$('[data-cb-atc]', this).forEach(b => b.disabled = !done);

    // Tier pills: highlight the highest tier currently achieved.
    const tiers = this.config.pricing?.tiers;
    if (Array.isArray(tiers) && tiers.length > 0) {
      const totalQty = this.state.totalQty;
      const matching = tiers
        .filter((t) => totalQty >= (t.qty || 0))
        .reduce((best, t) => ((t.percent || 0) > (best?.percent || 0) ? t : best), null);
      $$('[data-cb-tier-min]', this).forEach((el) => {
        const min = parseInt(el.dataset.cbTierMin || '0');
        if (matching && matching.qty === min) el.dataset.current = '';
        else delete el.dataset.current;
      });
    }

    // Mobile bar
    const count = $('.cb-drawer-bar__count', this);
    const price = $('.cb-drawer-bar__price', this);
    if (count) count.textContent =
      `${this.state.totalQty} item${this.state.totalQty !== 1 ? 's' : ''} selected`;
    if (price) price.textContent = fmt(this._displayPrice());

    // Category progress pills
    (this.config.categories ?? []).forEach(cat => {
      const pill = $(`[data-cb-progress="${cat.id}"]`, this);
      if (!pill) return;
      const n = this.state.categoryCount(cat.id);
      pill.textContent = `${n} / ${cat.maxQty}`;
      n >= (cat.minQty ?? 1) ? (pill.dataset.done = '') : delete pill.dataset.done;
      n > cat.maxQty ? (pill.dataset.over = '') : delete pill.dataset.over;
    });

    // Slot visualization
    $$('[data-cb-slot]', this).forEach((slot, i) => {
      const items = [...this.state.selections.values()];
      const item = items[i];
      if (item) {
        slot.dataset.filled = '';
        const img = $('img', slot);
        if (img) { img.src = item.variant.featured_image?.src || item.product.featured_image || ''; img.alt = item.product.title; }
        const label = $('[data-slot-label]', slot);
        if (label) label.textContent = item.product.title;
      } else {
        delete slot.dataset.filled;
        const img = $('img', slot);
        if (img) img.src = '';
        const label = $('[data-slot-label]', slot);
        if (label) label.textContent = slot.dataset.cbSlot || 'Empty';
      }
    });

    if (this._sidebar) this._sidebar.render();
  }

  _displayPrice() {
    const raw = this.state.totalCents;
    const pct = this.state.computeDiscountPct?.() ?? 0;
    if (this.state.config.pricing?.type === 'fixed' && this.state.totalQty > 0) {
      return Math.round((this.state.config.pricing.value ?? 0) * 100);
    }
    if (pct > 0) return Math.round(raw * (1 - pct / 100));
    return raw;
  }
}

// ── CanchaBundleCard ──────────────────────────────────────────

class CanchaBundleCard extends HTMLElement {
  connectedCallback() {
    if (this._connected) return;
    this._connected = true;

    const dataEl = $('[data-product-json]', this);
    try { this.productData = dataEl ? JSON.parse(dataEl.textContent) : null; }
    catch { this.productData = null; }

    this.categoryId = this.dataset.category || 'default';
    this.selectedVariant = this.productData?.variants?.find(v => v.available)
                        ?? this.productData?.variants?.[0];

    this._stateHandler = () => this._render();
    upgradeProperty(this, 'state');
    if (this._state) {
      this._state.removeEventListener('change', this._stateHandler);
      this._state.addEventListener('change', this._stateHandler);
    }

    this._bindEvents();
    this._render();
  }

  set state(s) {
    if (this._state && this._stateHandler) {
      this._state.removeEventListener('change', this._stateHandler);
    }
    this._state = s;
    if (s && this._stateHandler) s.addEventListener('change', this._stateHandler);
  }

  get state() { return this._state; }

  get _key() { return this.selectedVariant ? `${this.selectedVariant.id}__${this.categoryId}` : null; }

  get isSelected() { return this._key && this.state?.selections.has(this._key); }

  get currentQty() { return this.state?.selections.get(this._key)?.qty ?? 0; }

  _render() {
    const sel = this.isSelected;
    // CSS selectors target `.cb-card[data-selected]` (the inner div) — set
    // attributes there so styling actually applies. Mirror on the outer custom
    // element too so descendant selectors that target either work.
    const card = this.querySelector('.cb-card');
    if (card) {
      sel ? (card.dataset.selected = '') : delete card.dataset.selected;
      (sel && this.currentQty > 1)
        ? (card.dataset.qtyMultiple = '')
        : delete card.dataset.qtyMultiple;
    }
    sel ? (this.dataset.selected = '') : delete this.dataset.selected;
    (sel && this.currentQty > 1)
      ? (this.dataset.qtyMultiple = '')
      : delete this.dataset.qtyMultiple;

    const badge = $('.cb-card__qty-badge', this);
    if (badge) badge.textContent = sel ? (this.currentQty > 1 ? `×${this.currentQty}` : '1') : '';

    const qtyNum = $('.cb-card__qty-num', this);
    if (qtyNum) qtyNum.textContent = this.currentQty;
  }

  _bindEvents() {
    this.addEventListener('click', e => {
      if (e.target.closest('.cb-card__qty-btn, .cb-card__var-btn, .cb-card__swatch, [data-cb-card-add]')) return;
      this._toggle();
    });

    $('[data-cb-card-add]', this)?.addEventListener('click', e => { e.stopPropagation(); this._toggle(); });
    $('[data-qty-inc]', this)?.addEventListener('click', e => { e.stopPropagation(); this._inc(); });
    $('[data-qty-dec]', this)?.addEventListener('click', e => { e.stopPropagation(); this._dec(); });

    $$('.cb-card__var-btn, .cb-card__swatch', this).forEach(btn => {
      const vid = parseInt(btn.dataset.variantId);
      const variant = this.productData?.variants?.find(v => v.id === vid);
      if (variant && !variant.available) {
        btn.disabled = true;
        btn.dataset.sold = '';
        return;
      }
      btn.addEventListener('click', e => { e.stopPropagation(); this._pickVariant(btn); });
    });
  }

  _toggle() {
    if (!this.productData || !this.selectedVariant) return;
    if (this.isSelected) {
      this.state?.remove(this._key);
    } else {
      const ok = this.state?.add(this.productData, this.selectedVariant, this.categoryId);
      if (!ok) this._shakeProgress();
    }
  }

  _inc() {
    if (!this.isSelected) { this._toggle(); return; }
    const ok = this.state?.add(this.productData, this.selectedVariant, this.categoryId);
    if (!ok) this._shakeProgress();
  }

  _dec() { this._key && this.state?.decrement(this._key); }

  _pickVariant(btn) {
    const vid = parseInt(btn.dataset.variantId);
    const variant = this.productData?.variants?.find(v => v.id === vid);
    if (!variant || !variant.available) return;
    const wasSelected = this.isSelected;
    const oldKey = this._key;
    this.selectedVariant = variant;

    $$('.cb-card__var-btn, .cb-card__swatch', this).forEach(b => {
      const active = parseInt(b.dataset.variantId) === vid;
      active ? (b.dataset.active = '') : delete b.dataset.active;
    });

    // Swap the card's product image to the variant's image, if available.
    // Variant JSON exposes `featured_image: { src }` (matching Liquid's
    // variant.image lookup) — not `image` directly.
    const variantImage = variant.featured_image ?? variant.image;
    if (variantImage) {
      const img = $('.cb-card__img', this);
      if (img && img.tagName === 'IMG') {
        const url = typeof variantImage === 'string' ? variantImage : (variantImage.src ?? variantImage.url);
        if (url) img.src = url;
      }
    }

    if (wasSelected) {
      this.state?.changeVariant(oldKey, variant);
    } else {
      const ok = this.state?.add(this.productData, variant, this.categoryId);
      if (!ok) this._shakeProgress();
    }
    this._render();
  }

  _shakeProgress() {
    const pill = this.builder?.querySelector(`[data-cb-progress="${this.categoryId}"]`);
    pill?.animate([
      { transform: 'translateX(-3px)' }, { transform: 'translateX(3px)' },
      { transform: 'translateX(-3px)' }, { transform: 'translateX(0)' },
    ], { duration: 280 });
  }
}

// ── CanchaBundleSidebar ───────────────────────────────────────

class CanchaBundleSidebar extends HTMLElement {
  connectedCallback() {
    if (this._connected) return;
    this._connected = true;

    upgradeProperty(this, 'state');

    // Mobile bottom-sheet behaviour: only the peek toggle (chevron row) opens
    // and closes the sheet. The CTA stays addable, content scrolls freely.
    this._sheetHandler = (e) => {
      if (window.innerWidth > 1024) return;
      if (e.target.closest('[data-cb-peek-toggle]')) {
        this._toggleSheet();
      }
    };
    this.addEventListener('click', this._sheetHandler);

    // Close button (X) inside the sheet
    this.addEventListener('click', (e) => {
      if (e.target.closest('.cb-sheet-close')) {
        e.stopPropagation();
        this._closeSheet();
      }
    }, true);
  }

  _toggleSheet() {
    if (this.hasAttribute('data-open')) this._closeSheet();
    else this._openSheet();
  }
  _openSheet() {
    this.setAttribute('data-open', '');
    document.body.style.overflow = 'hidden';
  }
  _closeSheet() {
    this.removeAttribute('data-open');
    document.body.style.overflow = '';
  }

  set state(s) {
    this._state = s;
    s.addEventListener('change', () => this.render());
  }

  render() {
    if (!this._state) return;
    const state = this._state;

    // Items list
    const list = $('.cb-sidebar-items', this);
    const empty = $('.cb-sidebar-empty', this);
    if (list) {
      list.innerHTML = '';
      if (state.selections.size === 0) {
        empty?.removeAttribute('hidden');
      } else {
        empty?.setAttribute('hidden', '');
        for (const [key, item] of state.selections) {
          const el = document.createElement('div');
          el.className = 'cb-sidebar-item';
          el.innerHTML = `
            <img class="cb-sidebar-item__img" src="${item.variant.featured_image?.src || item.product.featured_image || ''}" alt="" width="44" height="44" loading="lazy">
            <div class="cb-sidebar-item__info">
              <div class="cb-sidebar-item__name">${item.product.title}</div>
              ${item.variant.title !== 'Default Title' ? `<div class="cb-sidebar-item__meta">${item.variant.title}${item.qty > 1 ? ` · ×${item.qty}` : ''}</div>` : (item.qty > 1 ? `<div class="cb-sidebar-item__meta">×${item.qty}</div>` : '')}
            </div>
            <button class="cb-sidebar-item__remove" aria-label="Remove" data-remove-key="${key}">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
            </button>`;
          el.querySelector('[data-remove-key]').addEventListener('click', () => state.remove(key));
          list.appendChild(el);
        }
      }
    }

    // Progress bar
    const fill = $('.cb-progress-fill', this);
    const meta = $('.cb-progress-meta span', this);
    const target = state.config.minQty ?? state.config.categories?.reduce((s, c) => s + (c.maxQty ?? 1), 0) ?? 1;
    if (fill) fill.style.width = Math.min(100, (state.totalQty / target) * 100) + '%';
    if (meta) meta.textContent = `${state.totalQty} / ${target}`;

    // Mobile peek-bar status text. While below the minimum, show progress
    // ("3 / 5 Products Selected"). Once met or exceeded, drop the slash so
    // it doesn't read as overflow ("7 / 2" looks like an error, not "done").
    const peekStatus = $('[data-cb-peek-status]', this);
    if (peekStatus) {
      const target = state.config.minQty
        ?? state.config.categories?.reduce((s, c) => s + (c.minQty ?? 1), 0)
        ?? 0;
      const noun = state.totalQty === 1 ? 'Product' : 'Products';
      peekStatus.textContent = target > 0 && state.totalQty < target
        ? `${state.totalQty} / ${target} ${noun} Selected`
        : `${state.totalQty} ${noun} Selected`;
    }

    // Price (update peek-bar + full sidebar). Strikethrough was-price +
    // colored savings pill carry the discount story.
    const priceEls   = $$('.cb-price-current', this);
    const wasEl      = $('.cb-price-was', this);
    const savingsEl  = $('[data-cb-savings]', this);

    if (priceEls.length > 0) {
      let display = state.totalCents;
      const pct = state.computeDiscountPct?.() ?? 0;
      if (state.config.pricing?.type === 'fixed' && state.totalQty > 0) {
        display = Math.round((state.config.pricing.value ?? 0) * 100);
      } else if (pct > 0) {
        display = Math.round(display * (1 - pct / 100));
      }

      const formatted = fmt(display);
      for (const el of priceEls) el.textContent = formatted;

      const savings = state.totalCents - display;
      if (wasEl) {
        if (savings > 0) {
          wasEl.textContent = fmt(state.totalCents);
          wasEl.hidden = false;
        } else { wasEl.hidden = true; }
      }
      if (savingsEl) {
        if (savings > 0) {
          savingsEl.textContent = `Save ${fmt(savings)}${pct > 0 ? ` (${pct}% off)` : ''}`;
          savingsEl.hidden = false;
        } else { savingsEl.hidden = true; }
      }
    }
  }
}

// ── CanchaBundleTabs ──────────────────────────────────────────

class CanchaBundleTabs extends HTMLElement {
  connectedCallback() {
    if (this._connected) return;
    this._connected = true;

    this._tabs   = $$('[role="tab"]', this);
    this._panels = $$('[role="tabpanel"]', this);
    this._tabs.forEach((tab, i) =>
      tab.addEventListener('click', () => this._activate(i))
    );
    this._activate(0);
  }

  _activate(i) {
    this._tabs.forEach((t, j) => {
      t.setAttribute('aria-selected', j === i);
    });
    this._panels.forEach((p, j) => { p.hidden = j !== i; });
  }
}

// ── CanchaBundleSteps (BYO guided mode) ───────────────────────

class CanchaBundleSteps extends HTMLElement {
  connectedCallback() {
    if (this._connected) return;
    this._connected = true;

    this._steps  = $$('[data-step]', this);
    this._panels = $$('[data-step-panel]', this);

    this._steps.forEach((step, i) =>
      step.addEventListener('click', () => {
        // Only allow navigating to completed or adjacent steps
        if (i <= this._current + 1) this._go(i);
      })
    );
    this._current = 0;
    this._update();
  }

  next() { this._go(Math.min(this._current + 1, this._steps.length - 1)); }
  prev() { this._go(Math.max(this._current - 1, 0)); }

  _go(i) {
    this._current = i;
    this._update();
  }

  _update() {
    this._steps.forEach((step, i) => {
      delete step.dataset.active;
      delete step.dataset.done;
      if (i === this._current) step.dataset.active = '';
      if (i < this._current)  step.dataset.done  = '';
    });
    this._panels.forEach((p, i) => { p.hidden = i !== this._current; });
  }
}

// ── Modal trigger wiring ──────────────────────────────────────

/**
 * Find the bottom of any sticky/fixed merchant theme header by sampling the
 * actual pixels at the top of the viewport. Selector-based detection is
 * brittle across the dozens of Shopify themes; this works regardless of
 * markup conventions.
 */

function openCanchaModal(modal) {
  // lazy images inside a closed dialog haven't fetched; flip the first grid's
  // worth to eager so the visible cards don't pop in one by one.
  modal.querySelectorAll('img[loading="lazy"]').forEach((img, i) => {
    if (i < 12) img.loading = 'eager';
  });
  // Non-modal dialogs aren't promoted to the top layer, so a parent with a
  // transform/filter or its own stacking context can trap the modal beneath
  // page chrome. Lifting it to <body> before opening escapes that.
  if (modal.parentElement !== document.body) {
    document.body.appendChild(modal);
  }
  // .show() (non-modal) instead of .showModal() so the merchant's site header
  // above the modal stays interactive — showModal() would put the page into
  // spec-mandated inert state and no CSS/JS could opt the header back in.
  if (typeof modal.show === 'function') {
    if (!modal.open) modal.show();
  } else {
    modal.removeAttribute('hidden');
  }
  document.body.style.overflow = 'hidden';
}

function closeCanchaModal(modal) {
  if (typeof modal.close === 'function' && modal.open) {
    modal.close();
  } else {
    modal.setAttribute('hidden', '');
  }
  document.body.style.overflow = '';
  // Clear the #bundle hash so the back button or refresh doesn't re-open the
  // modal in a loop. Only strip if it's the bundle hash — preserve other hashes.
  if (window.location.hash === '#bundle') {
    history.replaceState(null, '', window.location.pathname + window.location.search);
  }
}

/** Open the first bundle modal on the page if the URL has #bundle. */
function openBundleFromHash() {
  if (window.location.hash !== '#bundle') return;
  const modal = $('.cb-modal');
  if (modal) openCanchaModal(modal);
}

function initModalTriggers() {
  // Delegated listeners — one registration on document handles all existing
  // and future buttons including content injected by the global embed.
  if (!window.__canchaTriggersBound) {
    window.__canchaTriggersBound = true;

    document.addEventListener('click', e => {
      const btn = e.target.closest('[data-cb-open]');
      if (btn) {
        const root = btn.closest('[id^="cb-"]');
        const modal = root?.querySelector('.cb-modal');
        if (modal) openCanchaModal(modal);
        // No local modal — global embed's listener handles it.
        return;
      }
      const closeBtn = e.target.closest('[data-cb-close]');
      if (closeBtn) {
        const modal = closeBtn.closest('.cb-modal');
        if (modal) closeCanchaModal(modal);
        return;
      }
      // The dialog element itself is the dimmed overlay around the panel —
      // clicking it (outside .cb-panel) closes, like any lightbox.
      if (e.target.classList && e.target.classList.contains('cb-modal')) {
        closeCanchaModal(e.target);
      }
    });

    document.addEventListener('keydown', e => {
      if (e.key !== 'Escape') return;
      const pm = $('[data-cb-product-modal]:not([hidden])');
      if (pm) { closeProductModal(); return; }
      // Non-modal <dialog> doesn't get native ESC-to-close, so handle it here.
      const openModal = $('.cb-modal[open]');
      if (openModal) closeCanchaModal(openModal);
    });
  }

  // Dialog close cleanup — re-run on newly injected modals too.
  document.querySelectorAll('.cb-modal').forEach(modal => {
    if (modal.__canchaCloseListenerBound) return;
    modal.__canchaCloseListenerBound = true;
    modal.addEventListener('close', () => { document.body.style.overflow = ''; });
  });
}

// Expose API for the global embed so it can open a bundle by product handle
// without a page navigation, by fetching the product page HTML and injecting
// the pre-rendered bundle modal into the current document.
// handle -> Promise<HTMLDialogElement>. Keyed on the promise, not the element,
// so a prefetch already in flight when the customer clicks is awaited rather
// than duplicated.
const modalCache = new Map();

/**
 * Pull the builder's modal out of a product page.
 *
 * A product page can carry both the inline `cancha-bundle` section
 * (<section id="cb-page-...">, no modal) and the builder block
 * (<div id="cb-...">, modal inside). Only the latter is openable, and the
 * inline section often renders first, so match on the modal, not the id.
 */
async function fetchBundleWrapper(url) {
  const res = await fetch(url);
  if (!res.ok) return null;
  const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
  return Array.from(doc.querySelectorAll('[id^="cb-"]')).find((el) => el.querySelector('.cb-modal')) || null;
}

function resolveModal(handle) {
  if (modalCache.has(handle)) return modalCache.get(handle);

  const promise = (async () => {
    const path = '/products/' + encodeURIComponent(handle);

    // Full page fetch. NOT `?section_id=main` — section ids on JSON templates
    // are `template--<id>__main`, so a literal "main" is a guaranteed 404.
    const wrapper = await fetchBundleWrapper(path);

    if (!wrapper) {
      throw new Error(
        'No bundle builder found on ' + path +
        '. Add the Bundle Builder block to that product\'s template, and check the product has a cancha_bundle.config metafield.'
      );
    }

    let host = document.getElementById('cb-embed-' + handle);
    if (!host) {
      host = document.createElement('div');
      host.id = 'cb-embed-' + handle;
      document.body.appendChild(host);
    }
    host.replaceChildren(wrapper);
    if (window.customElements?.upgrade) window.customElements.upgrade(wrapper);

    const modal = wrapper.querySelector('.cb-modal');
    if (!modal) throw new Error('Bundle wrapper for ' + handle + ' has no .cb-modal');

    initModalTriggers();
    return modal;
  })();

  promise.catch(() => modalCache.delete(handle)); // let the next attempt retry
  modalCache.set(handle, promise);
  return promise;
}

window.__canchaBundle = {
  /** Open a modal that is already in the document (rendered inline by a block). */
  openModal: function (modal) {
    initModalTriggers();
    openCanchaModal(modal);
  },

  /** Warm the modal without showing it, so the click that follows is instant. */
  prefetch: function (handle) {
    return resolveModal(handle).catch((err) => {
      console.warn('[Cancha Bundle] prefetch failed for ' + handle + ':', err.message);
    });
  },

  openForHandle: async function (handle) {
    try {
      // Cache the modal element, not the host it was injected into:
      // openCanchaModal() relocates it to <body> to escape stacking contexts,
      // so re-querying the host finds nothing after the first open.
      const modal = await resolveModal(handle);
      openCanchaModal(modal);
    } catch (err) {
      console.error('[Cancha Bundle] could not open the bundle builder for ' + handle + ':', err);
      throw err;
    }
  },
};

// Contents-block code (product preview modal + horizontal scroller) lives in
// cancha-bundle-contents.js — loaded only by the bundle-contents block.

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initModalTriggers();
    openBundleFromHash();
  });
} else {
  initModalTriggers();
  openBundleFromHash();
}

// ── Register ──────────────────────────────────────────────────

const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };

define('cancha-bundle-card',    CanchaBundleCard);
define('cancha-bundle-sidebar', CanchaBundleSidebar);
define('cancha-bundle-tabs',    CanchaBundleTabs);
define('cancha-bundle-steps',   CanchaBundleSteps);
define('cancha-bundle-builder', CanchaBundleBuilder);

})();
